import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles, Edit3, FileText, RefreshCw, Copy, Bookmark, Trash2 } from 'lucide-react';
import { getEventPersonality } from '../lib/eventPersonality';
import { getTierPresetSuggestions } from '../lib/tierPresetSuggestions';
import { generateEventDescription } from '../lib/descriptionGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ──────────────────────────────────────────────────────────────────────
// Step definitions — single source of truth for order + options
// ──────────────────────────────────────────────────────────────────────
const STEPS = [
  { key: 'event_type',    title: 'Event Type',     subtitle: 'What kind of event are you hosting?',
    options: ['Private Experience', 'Curated Gathering', 'House Party', 'Club / Venue', 'Sporting Event', 'Custom'] },
  { key: 'guest_type',    title: 'Guest Type',     subtitle: 'Who is this event for?',
    options: ['Couples Only', 'Singles + Couples', 'Invite Only', 'General Admission', 'Custom'] },
  { key: 'access_style',  title: 'Access Style',   subtitle: 'How do guests get in?',
    options: ['Instant Access', 'Approval Required', 'Application + Review'] },
  { key: 'pricing_style', title: 'Pricing Style',  subtitle: 'How will you charge?',
    options: ['Free', 'Flat Rate', 'Tiered', 'Decide Later'] },
  { key: 'privacy_level', title: 'Privacy',        subtitle: 'How should this event be discovered?',
    options: ['Public Listing', 'Hidden Location', 'Fully Private'] },
  { key: 'basics',        title: 'Event Basics',   subtitle: 'The essentials.', options: null },
  { key: 'review',        title: 'Review',         subtitle: 'Everything look right?', options: null },
];

const TOTAL_STEPS = STEPS.length;

// ──────────────────────────────────────────────────────────────────────
// Reusable UI primitives
// ──────────────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  const pct = Math.round(((step + 1) / TOTAL_STEPS) * 100);
  return (
    <div className="w-full" data-testid="guided-progress">
      <div className="flex justify-between items-center mb-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#777]">
          Step {step + 1} of {TOTAL_STEPS}
        </span>
        <span className="text-[10px] font-bold text-[#D4AF37]">{pct}%</span>
      </div>
      <div className="h-1 w-full bg-[#1A1A1A] rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function OptionButton({ label, selected, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left rounded-sm border px-5 py-4 text-base font-medium transition-all
        ${selected
          ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#EAE7E1]'
          : 'border-[#1A1A1A] bg-[#0A0A0A] text-[#AFAFAF] hover:border-[#333] hover:text-[#EAE7E1]'}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span>{label}</span>
        {selected && <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />}
      </div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────
export default function GuidedEventBuilder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [applySuggestedTiers, setApplySuggestedTiers] = useState(false);
  const [descriptionSeed, setDescriptionSeed] = useState(0);
  const [confirmReplaceDescription, setConfirmReplaceDescription] = useState(false);
  // Phase 2D: personal tier presets
  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState(null);

  // Fetch on mount; silently swallow errors (feature is optional)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const r = await axios.get(`${API}/users/tier-presets`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setPresets(Array.isArray(r.data) ? r.data : []);
      } catch (_) {
        if (!cancelled) setPresets([]);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const [answers, setAnswers] = useState({
    event_type: '',
    guest_type: '',
    access_style: '',
    pricing_style: '',
    privacy_level: '',
    basics: {
      name: '',
      date: '',
      start_time: '',
      end_time: '',
      location: '',
      location_add_later: false,
      description: '',
    },
  });

  const currentStep = STEPS[step];
  const isBasics = currentStep.key === 'basics';
  const isReview = currentStep.key === 'review';
  const isOption = !!currentStep.options;

  // ────────────────────────────────────────────────────────────────────
  // Step validation — can user proceed to next step?
  // ────────────────────────────────────────────────────────────────────
  const canProceed = (() => {
    if (isOption) return !!answers[currentStep.key];
    if (isBasics) {
      const b = answers.basics;
      const hasLocation = b.location_add_later || (b.location || '').trim().length > 0;
      return !!b.name.trim() && !!b.date && !!b.start_time && hasLocation;
    }
    return true; // review
  })();

  const selectOption = (value) => {
    setAnswers((prev) => ({ ...prev, [currentStep.key]: value }));
  };

  const updateBasics = (field, value) => {
    setAnswers((prev) => ({ ...prev, basics: { ...prev.basics, [field]: value } }));
  };

  const goBack = () => {
    if (step === 0) navigate('/events/create');
    else setStep((s) => s - 1);
  };

  const goNext = () => {
    if (!canProceed) return;
    if (step < TOTAL_STEPS - 1) setStep((s) => s + 1);
  };

  const jumpToStep = (idx) => setStep(idx);

  // ────────────────────────────────────────────────────────────────────
  // Final submit
  // ────────────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('session_token');
      // Base payload — unchanged from Phase 2B-1 when checkbox is OFF
      const body = { ...answers };
      // Phase 2C: ... omitted comment
      // Phase 2D: opt-in only — include apply flag + suggested names
      if (applySuggestedTiers) {
        let suggestionNames;
        if (activePresetId) {
          const p = presets.find((x) => x.id === activePresetId);
          suggestionNames = (p?.tiers || []).map((t) => t.name);
        } else {
          suggestionNames = getTierPresetSuggestions({
            event_type: answers.event_type,
            guest_type: answers.guest_type,
            pricing_style: answers.pricing_style,
          }).map((s) => s.name);
        }
        body.apply_suggested_tiers = true;
        body.suggested_tiers = suggestionNames.map((name) => ({ name }));
      }
      const res = await axios.post(
        `${API}/events/guided-create`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Event created');
      const target = res.data?.redirect_to || `/events/${res.data?.event_id}/edit`;
      // Phase 2C: if the host applied a suggested description in the guided
      // builder, pass it through via router state so EditEvent can pre-fill
      // the Description textarea on initial load. Purely client-side — the
      // backend guided-create payload shape is unchanged.
      const guidedDescription = (answers.basics.description || '').trim();
      if (guidedDescription) {
        navigate(target, { state: { guidedDescription } });
      } else {
        navigate(target);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to create event';
      toast.error(typeof detail === 'string' ? detail : 'Failed to create event');
      setSubmitting(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────
  // Render helpers per step kind
  // ────────────────────────────────────────────────────────────────────
  const renderOptionStep = () => (
    <div className="space-y-3" data-testid={`guided-step-${currentStep.key}`}>
      {currentStep.options.map((opt) => (
        <OptionButton
          key={opt}
          label={opt}
          selected={answers[currentStep.key] === opt}
          onClick={() => selectOption(opt)}
          testid={`guided-option-${currentStep.key}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        />
      ))}
    </div>
  );

  const renderBasicsStep = () => {
    const b = answers.basics;
    return (
      <div className="space-y-5" data-testid="guided-step-basics">
        <div>
          <Label className="text-[#EAE7E1] text-sm">Event Name</Label>
          <Input
            value={b.name}
            onChange={(e) => updateBasics('name', e.target.value)}
            placeholder="Friday Night at The Velvet Room"
            className="mt-1.5 h-12 text-base"
            data-testid="guided-basics-name"
          />
        </div>
        <div>
          <Label className="text-[#EAE7E1] text-sm">Date</Label>
          <Input
            type="date"
            value={b.date}
            onChange={(e) => updateBasics('date', e.target.value)}
            className="mt-1.5 h-12 text-base"
            data-testid="guided-basics-date"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[#EAE7E1] text-sm">Start Time</Label>
            <Input
              type="time"
              value={b.start_time}
              onChange={(e) => updateBasics('start_time', e.target.value)}
              className="mt-1.5 h-12 text-base"
              data-testid="guided-basics-start-time"
            />
          </div>
          <div>
            <Label className="text-[#EAE7E1] text-sm">End Time</Label>
            <Input
              type="time"
              value={b.end_time}
              onChange={(e) => updateBasics('end_time', e.target.value)}
              className="mt-1.5 h-12 text-base"
              data-testid="guided-basics-end-time"
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label className="text-[#EAE7E1] text-sm">Location</Label>
            <label className="flex items-center gap-2 text-xs text-[#AFAFAF] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={b.location_add_later}
                onChange={(e) => updateBasics('location_add_later', e.target.checked)}
                className="accent-[#D4AF37] w-4 h-4"
                data-testid="guided-basics-location-later"
              />
              Add later
            </label>
          </div>
          <Input
            value={b.location}
            onChange={(e) => updateBasics('location', e.target.value)}
            placeholder={b.location_add_later ? 'TBA — you can add this later' : '123 Main St, City'}
            disabled={b.location_add_later}
            className="mt-1.5 h-12 text-base disabled:opacity-50"
            data-testid="guided-basics-location"
          />
        </div>
      </div>
    );
  };

  const renderReviewStep = () => {
    const b = answers.basics;
    const personality = getEventPersonality(answers.event_type);
    const rows = [
      { label: 'Event Type', value: answers.event_type, stepIdx: 0 },
      { label: 'Guest Type', value: answers.guest_type, stepIdx: 1 },
      { label: 'Access Style', value: answers.access_style, stepIdx: 2 },
      { label: 'Pricing Style', value: answers.pricing_style, stepIdx: 3 },
      { label: 'Privacy', value: answers.privacy_level, stepIdx: 4 },
      { label: 'Event Name', value: b.name, stepIdx: 5 },
      { label: 'Date', value: b.date, stepIdx: 5 },
      { label: 'Start Time', value: b.start_time, stepIdx: 5 },
      { label: 'End Time', value: b.end_time || '—', stepIdx: 5 },
      { label: 'Location', value: b.location_add_later ? 'Add later' : (b.location || '—'), stepIdx: 5 },
    ];
    return (
      <div className={`space-y-3 ${personality.themeClass}`} data-testid="guided-step-review">
        {/* Personality preview chip — read-only cue to the host about the copy
            guests will see on the public event page */}
        <div
          className="rounded-sm border p-3 text-xs"
          style={{
            borderColor: 'var(--event-border)',
            background: 'var(--event-panel-overlay)',
            color: 'var(--event-accent)',
          }}
          data-testid="guided-review-personality-preview"
        >
          <div className="font-bold uppercase tracking-wider text-[10px] mb-1.5">
            {personality.name} personality
          </div>
          <div className="text-[#B9B1A6] leading-relaxed">
            Guests will see <span data-testid="guided-review-preview-cta" className="font-semibold" style={{color:'var(--event-accent)'}}>"{personality.labels.ctaPrimary}"</span>{' '}
            as the CTA and this event as an <span data-testid="guided-review-preview-ticket" className="font-semibold" style={{color:'var(--event-accent)'}}>{personality.labels.ticket}</span>.{' '}
            Approved buyers are called <span data-testid="guided-review-preview-guests" className="font-semibold" style={{color:'var(--event-accent)'}}>{personality.labels.guests}</span>.
          </div>
        </div>
        <div className="rounded-sm border border-[#1A1A1A] bg-[#0A0A0A] divide-y divide-[#1A1A1A]">
          {rows.map((r) => (
            <button
              type="button"
              key={r.label}
              onClick={() => jumpToStep(r.stepIdx)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#111]"
              data-testid={`guided-review-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#777]">{r.label}</div>
                <div className="text-sm text-[#EAE7E1] truncate">{r.value || '—'}</div>
              </div>
              <Edit3 className="w-4 h-4 text-[#555] flex-shrink-0" />
            </button>
          ))}
        </div>
        <p className="text-xs text-[#777] px-1 pt-2">
          Flyer upload is skipped in Phase 1 — your event will use default VelvetGate branding.
          You can add a flyer and fine-tune everything on the next screen.
        </p>

        {/* ─── Suggested Tier Setup (preview-only, not submitted) ───────── */}
        {(() => {
          // Phase 2D: when a personal preset is active, show its tiers
          // instead of the algorithmic suggestions. Preset tier objects only
          // carry {name, attendee_count} so we synthesize price/note labels
          // from the same conventions used by tierPresetSuggestions.
          const activePreset = activePresetId ? presets.find((p) => p.id === activePresetId) : null;
          const suggestions = activePreset
            ? (activePreset.tiers || []).map((t) => ({
                name: t.name,
                priceLabel: 'Set price later',
                attendeeCountLabel: `${t.attendee_count} ${t.attendee_count === 1 ? 'attendee' : 'attendees'} per pass`,
                note: 'From your saved preset',
              }))
            : getTierPresetSuggestions({
                event_type: answers.event_type,
                guest_type: answers.guest_type,
                pricing_style: answers.pricing_style,
              });
          return (
            <div className="pt-5" data-testid="guided-review-tier-suggestions">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#EAE7E1] mb-1">
                Suggested Tier Setup
              </h3>
              <p className="text-xs text-[#777] mb-3">
                These are only suggestions. You can edit or replace them after creating the event.
              </p>

              {/* ── Phase 2D: My Presets selector (only render if any) ── */}
              {presets.length > 0 && (
                <div className="mb-3" data-testid="guided-review-my-presets">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#777] flex items-center gap-1.5">
                      <Bookmark className="w-3 h-3" style={{ color: 'var(--event-accent)' }} />
                      My Presets
                    </div>
                    {activePresetId && (
                      <button
                        type="button"
                        className="text-[10px] uppercase tracking-wider text-[#AFAFAF] hover:text-[#EAE7E1]"
                        onClick={() => setActivePresetId(null)}
                        data-testid="guided-presets-clear"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {presets.map((p) => {
                      const isActive = activePresetId === p.id;
                      const names = (p.tiers || []).map((t) => t.name).join(', ');
                      return (
                        <div
                          key={p.id}
                          className="rounded-sm border p-3 bg-[#0A0A0A] flex items-start gap-3"
                          style={{ borderColor: isActive ? 'var(--event-accent)' : '#1A1A1A' }}
                          data-testid={`guided-preset-card-${p.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-[#EAE7E1] truncate">{p.name}</div>
                            <div className="text-[11px] text-[#777] mt-0.5 break-words">{names}</div>
                          </div>
                          <div className="flex flex-col gap-1.5 flex-shrink-0">
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => setActivePresetId(isActive ? null : p.id)}
                              className={isActive
                                ? 'bg-[#1A1A1A] text-[#D4AF37] border border-[#D4AF37] rounded-sm h-8 px-2.5 text-[10px] font-bold'
                                : 'bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm h-8 px-2.5 text-[10px] font-bold'}
                              data-testid={`guided-preset-use-${p.id}`}
                            >
                              {isActive ? 'In Use' : 'Use Preset'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                if (!window.confirm(`Delete "${p.name}"?`)) return;
                                try {
                                  const token = localStorage.getItem('session_token');
                                  await axios.delete(`${API}/users/tier-presets/${p.id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  setPresets((prev) => prev.filter((x) => x.id !== p.id));
                                  if (activePresetId === p.id) setActivePresetId(null);
                                  toast.success('Preset deleted');
                                } catch (_) {
                                  toast.error('Failed to delete preset');
                                }
                              }}
                              className="text-[#777] hover:text-[#C44545] h-8 px-2"
                              data-testid={`guided-preset-delete-${p.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {suggestions.map((s, idx) => (
                  <div
                    key={`${s.name}-${idx}`}
                    className="rounded-sm border p-3 bg-[#0A0A0A]"
                    style={{
                      borderColor: 'var(--event-border)',
                      background: 'var(--event-panel-overlay)',
                    }}
                    data-testid={`guided-review-tier-suggestion-${idx}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <div
                        className="text-sm font-semibold"
                        style={{ color: 'var(--event-accent)' }}
                        data-testid={`guided-review-tier-suggestion-${idx}-name`}
                      >
                        {s.name}
                      </div>
                      <div
                        className="text-[10px] uppercase tracking-wider font-bold text-[#AFAFAF] whitespace-nowrap"
                        data-testid={`guided-review-tier-suggestion-${idx}-price`}
                      >
                        {s.priceLabel}
                      </div>
                    </div>
                    <div
                      className="text-xs text-[#B9B1A6]"
                      data-testid={`guided-review-tier-suggestion-${idx}-attendees`}
                    >
                      {s.attendeeCountLabel}
                    </div>
                    <div
                      className="text-[11px] text-[#777] mt-0.5 italic"
                      data-testid={`guided-review-tier-suggestion-${idx}-note`}
                    >
                      {s.note}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Opt-in: Create event with these suggested tiers ── */}
              <label
                className="mt-3 flex items-start gap-3 rounded-sm border border-[#1A1A1A] bg-[#0A0A0A] p-3 cursor-pointer hover:border-[#333] select-none"
                data-testid="guided-apply-suggested-tiers-label"
              >
                <input
                  type="checkbox"
                  checked={applySuggestedTiers}
                  onChange={(e) => setApplySuggestedTiers(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#D4AF37] flex-shrink-0"
                  data-testid="guided-apply-suggested-tiers-checkbox"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-[#EAE7E1]">
                    Create event with these suggested tiers
                  </span>
                  <span className="block text-xs text-[#777] mt-0.5">
                    You can still edit prices and details after the event is created.
                  </span>
                </span>
              </label>
            </div>
          );
        })()}

        {/* ─── Phase 2C: Suggested Description (preview + apply) ───── */}
        {(() => {
          const generated = generateEventDescription(
            {
              event_type: answers.event_type,
              guest_type: answers.guest_type,
              access_style: answers.access_style,
              privacy_level: answers.privacy_level,
            },
            descriptionSeed
          );
          const current = (answers.basics.description || '').trim();
          const isApplied = current === generated.trim() && current.length > 0;

          const applyDescription = () => {
            if (current && current !== generated.trim()) {
              // Host already has a different description — confirm replace
              setConfirmReplaceDescription(true);
              return;
            }
            setAnswers((prev) => ({
              ...prev,
              basics: { ...prev.basics, description: generated },
            }));
            toast.success('Description applied');
          };

          const doReplace = () => {
            setAnswers((prev) => ({
              ...prev,
              basics: { ...prev.basics, description: generated },
            }));
            setConfirmReplaceDescription(false);
            toast.success('Description replaced');
          };

          const copyDescription = async () => {
            try {
              await navigator.clipboard.writeText(generated);
              toast.success('Copied to clipboard');
            } catch (_) {
              toast.error('Copy not supported on this browser');
            }
          };

          return (
            <div className="pt-5" data-testid="guided-review-description-section">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#EAE7E1] mb-1 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" style={{ color: 'var(--event-accent)' }} />
                Suggested Description
              </h3>
              <p className="text-xs text-[#777] mb-3">
                Based on your selections. You can edit this anytime.
              </p>
              <div
                className="rounded-sm border p-3 bg-[#0A0A0A] text-sm text-[#EAE7E1] leading-relaxed whitespace-pre-wrap break-words"
                style={{ borderColor: 'var(--event-border)', background: 'var(--event-panel-overlay)' }}
                data-testid="guided-review-description-text"
              >
                {generated}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={applyDescription}
                  disabled={isApplied}
                  className="bg-[#D4AF37] hover:bg-[#F2D27A] disabled:bg-[#1A1A1A] disabled:text-[#555] text-[#050505] rounded-sm font-bold h-9 px-4 text-xs"
                  data-testid="guided-description-use-button"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  {isApplied ? 'Applied' : 'Use this description'}
                </Button>
                <Button
                  type="button"
                  onClick={copyDescription}
                  variant="outline"
                  className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm h-9 px-3 text-xs"
                  data-testid="guided-description-copy-button"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Copy
                </Button>
                <Button
                  type="button"
                  onClick={() => setDescriptionSeed((s) => s + 1)}
                  variant="outline"
                  className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm h-9 px-3 text-xs"
                  data-testid="guided-description-regenerate-button"
                >
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                  Regenerate
                </Button>
              </div>
              {isApplied && (
                <p className="mt-2 text-[11px] text-[#777]" data-testid="guided-description-applied-hint">
                  This description will be set on your event.
                </p>
              )}

              {/* Confirm-replace modal */}
              {confirmReplaceDescription && (
                <div
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
                  onClick={() => setConfirmReplaceDescription(false)}
                  data-testid="guided-description-replace-modal"
                >
                  <div
                    className="bg-[#0A0A0A] border-2 border-[#D4AF37] rounded-lg p-6 max-w-md w-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className="text-lg font-bold text-[#EAE7E1] mb-2">
                      Replace current description?
                    </h3>
                    <p className="text-sm text-[#B9B1A6] mb-5">
                      You already set a description. This will overwrite it with the suggested one.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button
                        type="button"
                        onClick={() => setConfirmReplaceDescription(false)}
                        variant="outline"
                        className="flex-1 border-[#333] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm"
                        data-testid="guided-description-replace-cancel"
                      >
                        Keep current
                      </Button>
                      <Button
                        type="button"
                        onClick={doReplace}
                        className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] text-[#050505] rounded-sm font-bold"
                        data-testid="guided-description-replace-confirm"
                      >
                        Replace
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    );
  };

  // ────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      {/* Header */}
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center justify-between gap-2 mb-4">
            <Button
              onClick={goBack}
              variant="ghost"
              className="-ml-2 text-[#AFAFAF] hover:text-[#EAE7E1] text-xs"
              data-testid="guided-back-header"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              {step === 0 ? 'Exit Guided' : 'Back'}
            </Button>
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
              <Sparkles className="w-3.5 h-3.5" />
              Guided Builder
            </div>
          </div>
          <ProgressBar step={step} />
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-32">
        <h1
          className="text-2xl sm:text-3xl font-bold text-[#EAE7E1] tracking-tight mb-2"
          data-testid="guided-step-title"
        >
          {currentStep.title}
        </h1>
        <p className="text-sm text-[#AFAFAF] mb-7">{currentStep.subtitle}</p>

        {isOption && renderOptionStep()}
        {isBasics && renderBasicsStep()}
        {isReview && renderReviewStep()}
      </div>

      {/* Sticky bottom nav */}
      <div className="fixed bottom-0 inset-x-0 border-t border-[#1A1A1A] bg-[#0A0A0A]/95 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Button
            onClick={goBack}
            variant="outline"
            disabled={submitting}
            className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm h-11 px-4"
            data-testid="guided-back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </Button>

          {isReview ? (
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="flex-1 sm:flex-initial bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm font-bold h-11 px-6"
              data-testid="guided-create-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Building your event...
                </>
              ) : (
                'Create Event'
              )}
            </Button>
          ) : (
            <Button
              onClick={goNext}
              disabled={!canProceed}
              className="flex-1 sm:flex-initial bg-[#D4AF37] hover:bg-[#F2D27A] disabled:bg-[#1A1A1A] disabled:text-[#555] text-[#050505] rounded-sm font-bold h-11 px-6"
              data-testid="guided-next-button"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
